import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { UserProvider } from "@/contexts/UserContext";
import { RootErrorBoundary } from "@/components/RootErrorBoundary";
import App from "./App.tsx";
import "./index.css";
import "./i18n/config";

/**
 * PRODUCTION CRASH FIX - HARDENED VERSION
 * 
 * Problem: Previously, ErrorBoundary was outside UserProvider. When an error occurred,
 * the fallback UI rendered outside the provider tree, and any component trying to use
 * useCurrentUser() would throw "must be used within a UserProvider" - causing cascading errors.
 * 
 * Solution: 
 * 1. UserProvider is now at the TOP of the component tree (ABOVE ErrorBoundary)
 * 2. RootErrorBoundary has a minimal fallback that doesn't use any context hooks
 * 3. useCurrentUser hook is fail-safe in production (returns defaults + logs error)
 * 4. Runtime assertions verify correct provider mounting at startup
 * 
 * PROVIDER ORDER (top to bottom) - CRITICAL:
 * 1. UserProvider     - Auth state, MUST be outermost so ErrorBoundary fallback can't break it
 * 2. RootErrorBoundary - Catches fatal errors with zero-dependency fallback UI
 * 3. HelmetProvider   - SEO meta tags
 * 4. App              - Contains QueryClient, ProfileProvider, Router, etc.
 * 
 * This order ensures:
 * - If any error occurs in App, the fallback renders inside UserProvider
 * - The fallback doesn't use useCurrentUser, so it can't crash
 * - Auth state remains stable even during error recovery
 */

// ============================================
// STARTUP ASSERTIONS & GUARDS
// ============================================

const isDev = import.meta.env.DEV;

// Guard: Verify single React root
if (isDev) {
  const rootEl = document.getElementById("root");
  if (rootEl && (rootEl as any).__reactRoot) {
    console.error(
      '❌ FATAL: Multiple React roots detected!',
      '\nThis will cause provider context issues.',
      '\nCheck for duplicate createRoot() calls.'
    );
  }
}

// Guard: Verify DOM is ready
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error(
    '[main.tsx] Root element #root not found in DOM. ' +
    'Ensure index.html contains <div id="root"></div>'
  );
}

// ============================================
// SERVICE WORKER HARDENING (stale chunk mitigation)
// ============================================
//
// Mixed old/new chunks (often due to an old Service Worker cache) can load two different
// copies of the same module graph, which can manifest as "useCurrentUser called outside
// UserProvider" even when the provider order is correct.
//
// Temporary mitigation:
// - In DEV: always unregister SW to avoid stale preview caches.
// - In PROD: allow a forced SW-unregister via ?nosw=1, then reload once.
//
if ("serviceWorker" in navigator) {
  const params = new URLSearchParams(window.location.search);
  const noSw = params.get("nosw") === "1";

  const unregisterAll = async () => {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
      console.log("[SW] Unregistered existing service workers");
    } catch {
      // ignore
    }
  };

  if (import.meta.env.DEV) {
    // DEV: always unregister to keep local iterations consistent.
    void unregisterAll();
  } else if (noSw) {
    // PROD: explicit debug switch to clear SW + caches and reload.
    void unregisterAll().finally(() => {
      // avoid infinite reload loops
      params.delete("nosw");
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}${window.location.hash}`;
      window.location.replace(next);
    });
  }
}

// ============================================
// REACT ROOT CREATION
// ============================================

const root = createRoot(rootElement);

// Mark root element to detect duplicate roots in dev
if (isDev) {
  (rootElement as any).__reactRoot = true;
}

// ============================================
// RENDER WITH CORRECT PROVIDER ORDER
// ============================================

root.render(
  <UserProvider>
    <RootErrorBoundary>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </RootErrorBoundary>
  </UserProvider>
);

// ============================================
// POST-MOUNT VERIFICATION (Dev only)
// ============================================

if (isDev) {
  // Verify providers mounted correctly after React renders
  requestAnimationFrame(() => {
    setTimeout(() => {
      const content = rootElement.textContent || '';
      if (content.includes('must be used within a UserProvider')) {
        console.error(
          '❌ [STARTUP CHECK FAILED] UserProvider error detected in rendered output!',
          '\nThe provider tree is misconfigured.',
          '\nCheck main.tsx provider order.'
        );
      } else {
        console.log('✅ [STARTUP CHECK] Provider tree mounted correctly');
      }
    }, 100);
  });
}
